// Background music: a looping track for the main menu and the tables, with
// crackling fireplace sounds looping quietly behind it.
//
// Shared <audio> elements play them, so going from the menu to a
// table and back does not restart it. It is made (and the file fetched) only
// the first time music should play: never on the login screen, never while
// the music or master volume is 0, and never as part of the loading screen.
// Browsers block audio until a user gesture: if play() is refused, it is
// retried on the next gesture (the same gestures that unlock game sounds).
import { UNLOCK_EVENTS } from "./audio";
import type { Settings } from "./settings";

export const MUSIC_FILE = "/music/soul-and-mind.mp3";
/** Fireplace crackle under the music (same Music volume slider). */
export const FIRE_FILE = "/music/fireplace.mp3";

/** Base loudness of the music before the volume settings (0..1). */
export const MUSIC_LEVEL = 0.5;
/** Base loudness of the fireplace: quieter, so it sits behind the music. */
export const FIRE_LEVEL = 0.3;

/** Final volume: base level x master x music (0 = muted). */
export function musicVolume(audio: Settings["audio"], level = MUSIC_LEVEL): number {
  return Math.min(1, Math.max(0, level * audio.master * audio.music));
}

export class Music {
  private el: HTMLAudioElement | null = null;
  private wanted = false;
  private volume = 0;
  private armed = false;
  private readonly onGesture = () => this.sync();

  constructor(
    private readonly file: string = MUSIC_FILE,
    private readonly level: number = MUSIC_LEVEL,
  ) {}

  /** Should music play now (main menu or a table)? */
  setActive(on: boolean): void {
    this.wanted = on;
    this.sync();
  }

  /** Back to the login screen: stop and rewind, so the next login starts from the top. */
  stop(): void {
    this.wanted = false;
    this.sync();
    if (this.el) this.el.currentTime = 0;
  }

  setVolume(audio: Settings["audio"]): void {
    this.volume = musicVolume(audio, this.level);
    if (this.el) this.el.volume = this.volume;
    this.sync();
  }

  private sync(): void {
    if (!this.wanted || this.volume <= 0) {
      this.disarm();
      if (this.el && !this.el.paused) this.el.pause();
      return;
    }
    const el = this.element();
    if (!el) return;
    el.volume = this.volume;
    if (!el.paused) {
      this.disarm();
      return;
    }
    let p: Promise<void> | undefined;
    try {
      p = el.play();
    } catch {
      this.arm();
      return;
    }
    // Older browsers return nothing; assume it plays.
    if (!p) return this.disarm();
    p.then(
      () => {
        this.disarm();
        // Turned off while play() was pending.
        if (!this.wanted || this.volume <= 0) el.pause();
      },
      (e: unknown) => {
        // NotAllowedError: no user gesture yet, so try again on the next one.
        // AbortError (paused meanwhile) or a missing/broken file: leave it.
        if ((e as { name?: string } | null)?.name === "NotAllowedError") this.arm();
      },
    );
  }

  private element(): HTMLAudioElement | null {
    if (this.el) return this.el;
    if (typeof Audio === "undefined") return null;
    const el = new Audio();
    el.loop = true;
    el.preload = "auto";
    el.volume = this.volume;
    el.src = this.file;
    this.el = el;
    return el;
  }

  private arm(): void {
    if (this.armed || typeof window === "undefined") return;
    this.armed = true;
    for (const type of UNLOCK_EVENTS) window.addEventListener(type, this.onGesture, { capture: true, passive: true });
  }

  private disarm(): void {
    if (!this.armed) return;
    this.armed = false;
    for (const type of UNLOCK_EVENTS) window.removeEventListener(type, this.onGesture, { capture: true });
  }
}

/** Several looping tracks driven together (the music and the fireplace). */
class Layers {
  constructor(private readonly tracks: Music[]) {}
  setActive(on: boolean): void {
    for (const t of this.tracks) t.setActive(on);
  }
  stop(): void {
    for (const t of this.tracks) t.stop();
  }
  setVolume(audio: Settings["audio"]): void {
    for (const t of this.tracks) t.setVolume(audio);
  }
}

export const music = new Layers([new Music(MUSIC_FILE, MUSIC_LEVEL), new Music(FIRE_FILE, FIRE_LEVEL)]);
